import { forwardRef, useRef, useState } from 'react'
import type { HTMLAttributes, PropsWithChildren, RefObject, TouchEvent } from 'react'
import styled from 'styled-components'

import { Spinner } from '../spinner/Spinner'

import { Color } from '../../theme/color'
import type { ColorKeys } from '../../theme/color'

interface ContentProps extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  backgroundColor?: ColorKeys
  isPullEvent?: boolean
  onPull?: () => void
}

export const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ children, backgroundColor, isPullEvent, onPull, ...props }, ref) => {
    const [isPulling, setIsPulling] = useState<boolean>(false)
    const [pullStartY, setPullStartY] = useState<number>(0)
    const [pullStartX, setPullStartX] = useState<number>(0)
    const [pullSize, setPullSize] = useState<number>(0)

    const contentRef = useRef<HTMLDivElement>(null)
    const currentRef = (ref || contentRef) as RefObject<HTMLDivElement>

    // 화면 터치(pull refresh) 시작
    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
      if (!isPullEvent) return

      props.onTouchStart?.(e)

      const scrollTop = currentRef.current?.scrollTop

      if ((scrollTop ?? 0) > 0) return

      const screenY = e.changedTouches[0].screenY
      const screenX = e.changedTouches[0].screenX

      setIsPulling(true)
      setPullStartY(screenY)
      setPullStartX(screenX)
    }

    // 화면 터치(pull refresh) 중
    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
      if (!isPullEvent || !isPulling) return

      props.onTouchMove?.(e)

      const scrollTop = currentRef.current?.scrollTop
      if ((scrollTop ?? 0) > 0) return

      const screenY = e.changedTouches[0].screenY
      const screenX = e.changedTouches[0].screenX

      const pullX = Math.max(pullStartX, screenX) - Math.min(pullStartX, screenX)
      const pullY = Math.max(pullStartY, screenY) - Math.min(pullStartY, screenY)

      const paddingTopSize = screenY - pullStartY

      if (paddingTopSize < 0 || pullX - pullY > 0) return

      if (currentRef.current) {
        if (paddingTopSize >= 120) return
        setPullSize(paddingTopSize)
      }
    }

    // 화면 터치(pull refresh) 끝
    const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
      if (!isPullEvent || !isPulling || pullSize <= 0) return

      props.onTouchEnd?.(e)

      if (currentRef.current) {
        const scrollTop = currentRef.current?.scrollTop
        if (scrollTop === 0 && pullSize > 100) onPull?.()

        setPullStartY(0)
        setPullSize(0)
      }

      setIsPulling(false)
    }

    return (
      <BaseContent
        {...props}
        ref={currentRef}
        backgroundColor={backgroundColor}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!!pullStartY && !!pullSize && (
          <LoadingBox $height={pullSize}>
            <Spinner color="#000000" />
          </LoadingBox>
        )}
        {children}
      </BaseContent>
    )
  },
)

const BaseContent = styled.div<ContentProps>`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${({ backgroundColor }) => (backgroundColor ? Color[backgroundColor] : Color.white)};
  > * {
    flex-shrink: 0;
  }
`

const LoadingBox = styled.div<{ $height: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${({ $height }) => $height}px;
`
